import * as React from "react";
import Box from "@mui/material/Box";
import "./index.css";
import { TextField, Grid, Autocomplete, Typography } from "@mui/material";
import {
  CollectionSite,
  GonotrophicEnum,
  LifeStageEnum,
  SexEnum,
  Specimen,
  SpecimenFormData,
} from "../../types";
import {
  GenusSpeciesAutoCompleteOptions,
  gonotrophicAutoCompleteOptions,
  lifeStatusAutoCompleteOptions,
  sexOptions,
} from "../../utils/constants";
import { parseGenusSpecies } from "../../utils/stringHelper";

interface Props {
  handleClose?: () => void;
  handleSave?: (data: CollectionSite) => void;
  formData?: CollectionSite;
  specimen: Specimen | null;
  onSubmit: (specimenId: string, collectionId: string, data: SpecimenFormData) => void;
}

const SpecimenForm: React.FC<Props> = ({ specimen, onSubmit }) => {
  const [dataForm, setDataForm] = React.useState<SpecimenFormData>({
    species: "",
    tags: [],
    sex: null,
    lifeStage: null,
    gonotrophic: null,
    notes: "",
  });
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (specimen) {
      setDataForm({
        species: specimen.species,
        tags: specimen.tags || [],
        sex: specimen.sex,
        lifeStage: specimen.lifeStage,
        gonotrophic: specimen.gonotrophic,
        notes: specimen.notes,
      });
    }
  }, [specimen]);

  const handleChange =
    (name: keyof SpecimenFormData) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDataForm({
        ...dataForm,
        [name]: value,
      });
      delayedSubmit({
        ...dataForm,
        [name]: value,
      });
    };

  const handleAutocompleteChange =
    (name: keyof SpecimenFormData) =>
    (
      _: React.SyntheticEvent,
      value:
        | string
        | string[]
        | SexEnum
        | LifeStageEnum
        | GonotrophicEnum
        | null
    ) => {
      setDataForm({
        ...dataForm,
        [name]: value,
      });

      delayedSubmit({
        ...dataForm,
        [name]: value,
      });
    };

  const delayedSubmit = (formData: SpecimenFormData) => {
    if (timer) {
      clearTimeout(timer);
    }
    // Set a new timer to submit the form after 0.5 seconds
    setTimer(
      setTimeout(() => {
        handleSubmit(formData);
      }, 1000)
    );
  };

  const handleSubmit = (formData: SpecimenFormData) => {
    if (specimen?.id) {
      onSubmit(specimen?.id, specimen?.collectionId || "", formData);
    }
  };

  return (
    <>
      <Box className="form-specimen" component="form">
        <Grid container spacing={2} sx={{
                            boxShadow: '-4px 4px 8px rgba(0, 0, 0, 0.2)',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: '#fff'
                          }}>
          <Grid size={12}>
            <Typography variant="h6">Algorithm Id:</Typography>
          </Grid>
          <Grid size={8}>
            <Box sx={{fontStyle: 'italic'}}>{parseGenusSpecies(specimen?.algorithmId?.genus + " " + specimen?.algorithmId?.species)}</Box>
          </Grid>
          <Grid size={4}>
            <Typography>
              {`${((specimen?.algorithmId?.confidence || 0) * 100)?.toFixed(
                2
              )}%`}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6" align={"center"}>
              Contributed Data
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 8 }}>
            <Autocomplete
              id="disable-close-on-select"
              value={dataForm.species}
              options={GenusSpeciesAutoCompleteOptions}
              renderInput={(params) => (
                <TextField {...params} label="Species" />
              )}
              onChange={handleAutocompleteChange("species")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 4 }}>
            <Autocomplete
              value={dataForm.sex}
              id="disable-close-on-select"
              options={sexOptions}
              renderInput={(params) => <TextField {...params} label="Sex" />}
              onChange={handleAutocompleteChange("sex")}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              value={dataForm.gonotrophic}
              id="disable-close-on-select"
              options={gonotrophicAutoCompleteOptions}
              renderInput={(params) => (
                <TextField {...params} label="Gonotrophic" />
              )}
              onChange={handleAutocompleteChange("gonotrophic")}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              value={dataForm.lifeStage}
              id="disable-close-on-select"
              options={lifeStatusAutoCompleteOptions}
              renderInput={(params) => (
                <TextField {...params} label="Life Stage" />
              )}
              onChange={handleAutocompleteChange("lifeStage")}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              freeSolo
              multiple
              disablePortal
              options={[]}
              value={dataForm.tags}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Specimen Tags" />
              )}
              onChange={handleAutocompleteChange("tags")}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Notes"
              fullWidth
              value={dataForm.notes}
              onChange={handleChange("notes")}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SpecimenForm;
